import { memo, type RefObject } from "react"
import { faImage, faTrophy } from "@fortawesome/pro-solid-svg-icons"
import { Box, Grid, IconButton, Link, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { ProductTableRow, ProductTableRowType } from "./productsTypes"
import { StockChip } from "./components/stockManagement/StockChip"
import { tailwindPalette } from "../../../theme/tailwindPalette"
import { MatchEnvironment } from "../../../environment/components/MatchEnvironment"
import { EnvironmentType } from "../../../environment/envTypes"
import { ProductInfoIcons, ProductGroupInfoIcons } from "./components/tableElements/ProductInfoIcons"

const useStyles = makeStyles()(() => ({
    photoIconButton: {
        padding: 0,
    },
    photoIcon: {
        fontSize: "1rem",
        color: tailwindPalette.slate[400],
    },
    soldTodayLabel: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".25rem",
        borderRadius: ".75rem",
        padding: ".125rem .375rem",
        whiteSpace: "nowrap",
        color: tailwindPalette.slate[700],
        border: `1px solid ${tailwindPalette.slate[100]}`,
        fontSize: ".75rem",
    },
    soldTodayLabelDoingWell: {
        color: tailwindPalette.green[700],
        border: `1px solid ${tailwindPalette.green[100]}`,
    },
    trophyIcon: {
        width: "1rem",
        height: ".75rem",
    },
    clickableName: {
        cursor: "pointer",
        color: "inherit",
        textDecoration: "none",
        "&:hover": {
            textDecoration: "underline",
        },
    },
    secondLine: {
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        marginTop: ".5rem",
    },
}))

type ProductGroupItemBoxProps = Readonly<{
    productGroup: ProductTableRow
    onGroupClick?: (groupId: number) => void
}>

export const ProductGroupItemBox = memo(function ProductGroupItemBox({
    productGroup,
    onGroupClick,
}: ProductGroupItemBoxProps) {
    const { classes } = useStyles()

    return (
        <Box>
            <Link
                component="button"
                variant="body1"
                fontWeight="bold"
                className={classes.clickableName}
                onClick={() => onGroupClick?.(productGroup.id)}
            >
                {productGroup.name}
            </Link>
            {productGroup.type === ProductTableRowType.GROUP && (
                <Box className={classes.secondLine}>
                    <ProductGroupInfoIcons group={productGroup} />
                </Box>
            )}
        </Box>
    )
})

type ProductNameProps = Readonly<{
    product: ProductTableRow
    onProductPhotoClick: (imageUrl: string) => void
    onStockChipClick?: (event: React.MouseEvent<HTMLElement>, productId: number) => void
    activeStockProductIdRef: RefObject<number | null>
    onProductClick?: (productId: number) => void
}>

export const ProductName = memo(function ProductName({
    product,
    onProductPhotoClick,
    onStockChipClick,
    activeStockProductIdRef,
    onProductClick,
}: ProductNameProps) {
    const { classes, cx } = useStyles()
    const productPhotoUrl = product.productPhoto?.url

    return (
        <Box>
            <Grid container item gap="0.75rem" alignItems="center" wrap="nowrap">
                <Grid item>
                    <Link
                        component="button"
                        variant="body2"
                        className={classes.clickableName}
                        onClick={() => onProductClick?.(product.id)}
                    >
                        {product.name}
                    </Link>
                </Grid>
                {productPhotoUrl && (
                    <Grid item>
                        <IconButton
                            onClick={() => onProductPhotoClick(productPhotoUrl)}
                            className={classes.photoIconButton}
                        >
                            <FontAwesomeIcon icon={faImage} className={classes.photoIcon} />
                        </IconButton>
                    </Grid>
                )}
                <MatchEnvironment environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.Demo]}>
                    {product.type === ProductTableRowType.PRODUCT && product.stock && (
                        <Grid item>
                            <StockChip
                                stock={product.stock}
                                isOpen={activeStockProductIdRef.current === product.id}
                                onClick={(e) => onStockChipClick?.(e, product.id)}
                            />
                        </Grid>
                    )}
                </MatchEnvironment>
            </Grid>
            {product.type === ProductTableRowType.PRODUCT && (
                <Box className={classes.secondLine}>
                    <ProductInfoIcons product={product} />
                    <MatchEnvironment
                        environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.Demo]}
                    >
                        {product.stock?.sales?.daily != null &&
                            !!product.stock.sales.daily.itemsForSale &&
                            (() => {
                                const total = product.stock.sales.daily.itemsForSale
                                const sold = product.stock.sales.daily.sold ?? 0
                                const isDoingWell = total > 0 && sold / total >= 0.8

                                return (
                                    <Box
                                        component="span"
                                        className={cx(
                                            classes.soldTodayLabel,
                                            isDoingWell && classes.soldTodayLabelDoingWell,
                                        )}
                                    >
                                        {isDoingWell && (
                                            <FontAwesomeIcon
                                                icon={faTrophy}
                                                className={classes.trophyIcon}
                                                color={tailwindPalette.amber[500]}
                                            />
                                        )}
                                        <LocalizedStrict id="product-stock-daily-sales" vars={{ sold, total }}>
                                            <Typography
                                                variant="caption"
                                                fontWeight={500}
                                            >{`Sold today: ${sold}/${total}`}</Typography>
                                        </LocalizedStrict>
                                    </Box>
                                )
                            })()}
                    </MatchEnvironment>
                </Box>
            )}
        </Box>
    )
})
